import { costOf, type Cost } from "../../lib/pricing";

/**
 * What one model did on one turn, for the activity sidebar.
 *
 * `model` is what the worker says actually served the turn, which can differ from
 * what was requested — a dated snapshot, or a fallback. Displaying the request would
 * defeat the point of asking.
 */
export type TurnLogEntry = {
  id: string;
  channelId: string;
  /** The model requested for this channel. */
  requestedModel: string;
  /** What the worker reports actually answered. */
  servedModel?: string;
  provider?: "openai" | "anthropic";
  inputTokens?: number;
  outputTokens?: number;
  elapsedMs: number;
  cost: Cost | null;
  at: Date;
};

export function makeLogEntry(input: {
  channelId: string;
  requestedModel: string;
  servedModel?: string;
  provider?: "openai" | "anthropic";
  inputTokens?: number;
  outputTokens?: number;
  elapsedMs: number;
}): TurnLogEntry {
  return {
    id: `${input.channelId}:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`,
    ...input,
    // Priced off the SERVED model — rating the requested one would misprice any
    // turn the provider substituted.
    cost: costOf(input.servedModel ?? input.requestedModel, input.inputTokens, input.outputTokens),
    at: new Date(),
  };
}

/** Whether the model that answered differs from the one asked for — worth flagging. */
export function wasSubstituted(entry: TurnLogEntry): boolean {
  if (!entry.servedModel) return false;
  const rolling = entry.servedModel.replace(/-(?:\d{4}-\d{2}-\d{2}|\d{8})$/, "");
  return rolling !== entry.requestedModel && entry.servedModel !== entry.requestedModel;
}
